import React, { useCallback } from 'react'
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Container,
  Box,
} from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import Link from '../../Link'
import {
  ExternalLink,
  NavigationGroup as NavigationGroupType,
} from '../../../types/page'
import { getExternalLinkURL } from '../../../utils/url'
import useUser from '../../../hooks/useUser'
import { lowerCased, titleCased } from '../../../utils/string'
import useSiteMetadata from '../../../hooks/useSiteMetadata'
import useBrand from '../../../hooks/useBrand'

interface AccordionClasses {
  root?: string
  expandIconWrapper?: string
  accordionDetails?: string
  accordionSummaryContent?: string
  accordionSummaryExpanded?: string
  accordionDetailEntry?: string
  accEntryLink?: string
}
interface AccountDropdownProps {
  accordionClass?: string
  accordionClasses?: AccordionClasses
  expanded?: string | false
}

interface AccountDropdownDesktopProps extends AccountDropdownProps {
  handleChange?: (s: string) => void
  navGroup: NavigationGroupType<ExternalLink>
}

interface DropdownChildrenProps {
  navGroup: NavigationGroupType<ExternalLink>
  classes: any
  cx: any
  accordionClasses?: AccordionClasses
}

export const AccountDropdownDetails: React.FC<DropdownChildrenProps> = ({
  navGroup,
  classes,
  cx,
  accordionClasses,
}) => {
  const {
    viewable_critique: viewableCritique,
    lead_auth_token: leadAuthToken,
  } = useUser()
  const { defaultResumeProduct } = useSiteMetadata()
  const { isTopCvFr } = useBrand()

  const getCritiqueLink = useCallback(() => {
    if (isTopCvFr) {
      return `/apercuevaluation${lowerCased(
        defaultResumeProduct
      )}?utm_content=cta_view_your_results&utm_source=apercuevaluation${lowerCased(
        defaultResumeProduct
      )}critique_funnel&utm_medium=email&utm_campaign=cr_normative_20240214_cr_fr&at=${leadAuthToken}&dt={{discountToken}}`
    }

    return `/${lowerCased(
      defaultResumeProduct
    )}critiqueview?at=${leadAuthToken}&dt={{discountToken}}`
  }, [defaultResumeProduct, isTopCvFr, leadAuthToken])

  return (
    <AccordionDetails
      className={cx(
        classes.accordionDetails,
        accordionClasses?.accordionDetails
      )}
      id={`accordion-details-${navGroup.title}`}
    >
      <Container
        disableGutters
        className={cx(
          classes.linkContainer,
          accordionClasses?.accordionDetailEntry
        )}
      >
        <Link
          href="/account-history"
          className={accordionClasses?.accEntryLink}
        >
          Account Dashboard
        </Link>
      </Container>
      <Container
        disableGutters
        className={cx(
          classes.linkContainer,
          accordionClasses?.accordionDetailEntry
        )}
      >
        <Link
          href={getExternalLinkURL(navGroup.links[0])}
          className={accordionClasses?.accEntryLink}
        >
          Your Order
        </Link>
      </Container>
      {viewableCritique && (
        <Container
          disableGutters
          className={cx(
            classes.linkContainer,
            accordionClasses?.accordionDetailEntry
          )}
        >
          <Link
            href={getCritiqueLink()}
            target="_blank"
            className={accordionClasses?.accEntryLink}
          >
            {`Your ${titleCased(defaultResumeProduct)} Critique`}
          </Link>
        </Container>
      )}
      <Container
        disableGutters
        className={cx(
          classes.linkContainer,
          accordionClasses?.accordionDetailEntry
        )}
      >
        <Link
          className={cx(classes.logoutBtn, accordionClasses?.accEntryLink)}
          href="/logout"
        >
          Logout
        </Link>
      </Container>
    </AccordionDetails>
  )
}

const AccountDropdownDesktop: React.FC<AccountDropdownDesktopProps> = ({
  handleChange,
  expanded,
  accordionClass,
  accordionClasses,
  navGroup,
}) => {
  const { classes, cx } = useStyles()

  return (
    <Box onMouseLeave={handleChange ? () => handleChange('') : () => null}>
      <Accordion
        expanded={expanded === `accordion-details-${navGroup.title}`}
        onMouseOver={
          handleChange
            ? () => handleChange(`accordion-details-${navGroup.title}`)
            : () => {
                /* do nothing */
              }
        }
        classes={{
          root: cx(classes.accordion, accordionClass, accordionClasses?.root),
          expanded: classes.accordionExpanded,
        }}
      >
        <AccordionSummary
          aria-controls={`accordion-details-${navGroup.title}`}
          classes={{
            root: classes.accordionSummary,
            content: classes.accordionSummaryContent,
            expanded: accordionClasses?.accordionSummaryExpanded,
          }}
          expandIcon={null}
        >
          {navGroup.links.map((link) => (
            <Link
              key={link.contentful_id}
              href="/account-history"
              className={classes.accountIcon}
            >
              {link.icon ? (
                <img alt={link.text} src={link.icon.file.url} />
              ) : (
                link.text
              )}
            </Link>
          ))}
        </AccordionSummary>
        <AccountDropdownDetails
          navGroup={navGroup}
          classes={classes}
          cx={cx}
          accordionClasses={accordionClasses}
        />
      </Accordion>
    </Box>
  )
}

const useStyles = makeStyles()((theme) => ({
  accordion: {
    margin: '0',
    '&:before': {
      display: 'none',
    },
    '& *': {
      '&:focus': {
        outline: 'none',
      },
    },
    [theme.breakpoints.down('lg')]: {
      padding: '0 6% 0 6%',
    },
    [theme.breakpoints.up('lg')]: {
      backgroundColor: theme.palette.background.default,
    },
  },
  accordionDetails: {
    backgroundColor: theme.palette.background.default,
    borderTop: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    flexDirection: 'column',
    padding: '1rem 0',
    position: 'absolute',
    minWidth: '10em',
    top: '4.5em',
    right: 0,

    [theme.breakpoints.down('lg')]: {
      borderTop: 'none',
      padding: '0 0 .54em',
      position: 'unset',
      width: '100%',
    },
  },
  accordionExpanded: {
    margin: '0 !important',
  },
  accordionSummary: {
    margin: '0',
    padding: '0',
    // minHeight: '0',
    maxHeight: '4rem',
  },
  accordionSummaryContent: {
    margin: 0,

    [theme.breakpoints.down('lg')]: {
      fontSize: '1.875rem',
      fontWeight: 700,
      padding: '1rem 0',
    },
  },
  icon: {
    color: theme.palette.text.primary,
    fontSize: '1.25rem',
  },
  accountIcon: {
    display: 'flex',
    margin: '1.4em 0',
  },
  linkContainer: {
    fontSize: '.94rem',
    lineHeight: '1.53em',
    padding: '.25rem 1.25rem',

    '&& *': {
      color: `${theme.palette.text.secondary}`,
    },

    [theme.breakpoints.down('lg')]: {
      padding: '.5rem 0',
    },
  },
  logoutBtn: {
    cursor: 'pointer',
  },
}))

export default AccountDropdownDesktop
